import React, { useState, useEffect } from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';
import { Link } from 'react-router-dom';
import { X, Menu, ChevronRight, Sun, Moon, ShieldPlus } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { trackEvent } from '../../lib/analytics';

const navLinks = [
  { label: 'Home', href: '#hero' },
  { label: 'Products', href: '#products' },
  { label: 'Innovation', href: '#innovation' },
  { label: 'Material Phases', href: '#showcase' },
  { label: 'Research', href: '#research' },
  { label: 'Contact', href: '#footer' },
];

const StickyNavbar = ({ onOpenContact }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 80, damping: 30 });

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNav = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <motion.div
        data-testid="scroll-progress-bar"
        className="fixed top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-blue-500 via-blue-400 to-[#E87A20] z-[60] origin-left"
        style={{ scaleX }}
      />
      <nav
        data-testid="sticky-navbar"
        className={`fixed top-[2px] left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-slate-950/95 backdrop-blur-xl border-b border-slate-800/50'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-8 flex items-center justify-between h-16">
          <a href="https://www.fmsmedequip.com/" target="_blank" rel="noopener noreferrer" className="group flex items-center gap-3" data-testid="navbar-logo">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center transition-transform duration-500 group-hover:scale-105">
              <img src="/fmsi-primary-transparent.png" alt="Fortschritt Medical Supplies Inc. — exclusive FlexiOss distributor in the Philippines" className="h-full w-full object-contain" />
            </div>
            <div className="hidden leading-none sm:block">
              <span className="block font-display text-lg font-bold not-italic uppercase tracking-[0.16em] text-white">FlexiOss<span className="text-[#E87A20]">®</span></span>
              <span className="mt-1 block text-[9px] uppercase tracking-[0.3em] text-slate-400">Distributed by FMSi</span>
            </div>
          </a>
          <div className="hidden xl:flex items-center gap-4">
            {navLinks.map((link) => (
              <button key={link.href} onClick={() => handleNav(link.href)} className="group relative text-sm font-medium text-slate-300 transition-colors hover:text-white" data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}>
                {link.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-[#E87A20] transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
            <button onClick={toggleTheme} data-testid="theme-toggle" aria-label="Toggle light or dark theme" className="p-2.5 text-slate-300 hover:text-white transition-colors">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <Link
              to="/reimbursement-guide"
              onClick={() => trackEvent('cta_click', { location: 'navbar', action: 'philhealth_rvs' })}
              data-testid="nav-philhealth-rvs-btn"
              aria-label="Open the PhilHealth RVS reimbursement page"
              className="inline-flex items-center gap-2 bg-[#006837] px-4 py-2.5 text-xs font-bold uppercase tracking-[0.1em] text-white ring-1 ring-[#FFC72C]/40 transition-colors duration-300 hover:bg-[#00522b]"
            >
              <ShieldPlus className="h-4 w-4" strokeWidth={2} /> PhilHealth RVS
            </Link>
            <button onClick={() => { trackEvent('cta_click', { location: 'navbar', action: 'pricing' }); onOpenContact('pricing'); }} data-testid="nav-pricing-btn" className="border border-[#E87A20] px-4 py-2.5 text-xs font-bold uppercase tracking-[0.1em] text-[#E87A20] transition-all duration-300 hover:bg-[#E87A20] hover:text-white">
              Get Pricing
            </button>
            <button onClick={() => { trackEvent('cta_click', { location: 'navbar', action: 'samples' }); onOpenContact('samples'); }} data-testid="nav-contact-btn" className="bg-blue-600 px-4 py-2.5 text-xs font-bold uppercase tracking-[0.1em] text-white transition-all duration-300 hover:bg-blue-500">
              Request Samples
            </button>
          </div>
          <div className="xl:hidden flex items-center gap-1">
            <button onClick={toggleTheme} data-testid="theme-toggle-mobile" aria-label="Toggle light or dark theme" className="p-2 text-white">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button className="p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
              {mobileOpen ? <X className="w-6 h-6 text-white" /> : <Menu className="w-6 h-6 text-white" />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="xl:hidden bg-slate-900/98 backdrop-blur-xl border-b border-slate-800">
            <div className="px-4 py-4 space-y-1">
              {navLinks.map((link) => (
                <button key={link.href} onClick={() => handleNav(link.href)} className="flex items-center justify-between w-full px-4 py-3 rounded-lg text-white font-semibold text-sm hover:bg-slate-800 transition-colors">
                  {link.label}
                  <ChevronRight className="w-4 h-4 text-blue-400" />
                </button>
              ))}
              <Link to="/reimbursement-guide" onClick={() => { setMobileOpen(false); trackEvent('cta_click', { location: 'navbar_mobile', action: 'philhealth_rvs' }); }} data-testid="mobile-philhealth-rvs-btn" className="flex w-full items-center justify-center gap-2 mt-2 bg-[#006837] text-white ring-1 ring-[#FFC72C]/40 font-bold uppercase tracking-[0.08em] px-5 py-3 text-sm">
                <ShieldPlus className="w-4 h-4" /> PhilHealth RVS Page
              </Link>
              <button onClick={() => { setMobileOpen(false); trackEvent('cta_click', { location: 'navbar_mobile', action: 'pricing' }); onOpenContact('pricing'); }} data-testid="mobile-pricing-btn" className="w-full mt-2 border border-[#E87A20] text-[#E87A20] font-bold uppercase tracking-[0.08em] px-5 py-3 text-sm">
                Get Pricing &amp; Quote
              </button>
              <button onClick={() => { setMobileOpen(false); trackEvent('cta_click', { location: 'navbar_mobile', action: 'samples' }); onOpenContact('samples'); }} data-testid="mobile-contact-btn" className="w-full mt-2 bg-blue-600 hover:bg-blue-500 text-white font-bold uppercase tracking-[0.08em] px-5 py-3 text-sm">
                Request for Samples
              </button>
            </div>
          </motion.div>
        )}
      </nav>
    </>
  );
};

export default StickyNavbar;
